/**
 * Detecta que el alumno terminó de hablar en una toma del micrófono, con la
 * energía del audio (Emmanuel, 15/10: «si lo apretás una vez, que detecte cuando
 * te callaste, como en el modo charla»). Mide ventanas de 50 ms: es voz lo que
 * pasa el umbral de la charla asistida (`createVoiceCounter`: rms 0,008, o el 10 %
 * del pico de la toma, para un micrófono que llega bajo). Corta cuando, después de
 * algo de voz, hubo `silenceMs` seguidos de silencio; sin voz todavía no corta (el
 * tope de la toma se encarga). Sigue la toma desde el principio, pero sólo corta
 * si se armó (`arm`): al mantener apretado manda soltar.
 *
 * El umbral tampoco queda debajo del ruido de fondo de la toma (Emmanuel, 20/10:
 * «Help me decide», dicha bajito en una pieza con ruido, no cortaba nunca: el 10 %
 * del pico quedaba debajo del ruido y el ruido contaba como voz). El ruido es la
 * ventana más baja de la toma; el umbral sube a 2,5 veces eso, sin pasar el 30 %
 * del pico (una toma que arranca hablando no se come su propia voz).
 *
 * Lo usan la toma en WAV del repaso (`feed`, con las muestras) y la hoja de la
 * charla asistida (`window`, con la energía que mide el micrófono del composer).
 */
const WINDOW_MS = 50;
const VOICE_ABS = 0.008;
const VOICE_REL = 0.1;
/** Cuántas veces el ruido de fondo hace falta para que sea voz. */
const NOISE_MULT = 2.5;
/** El umbral por ruido no pasa de esta parte del pico de la toma. */
const NOISE_CAP_REL = 0.3;
/** Voz mínima antes de cortar por silencio: un ruido suelto no es «ya habló». */
const MIN_VOICE_MS = 200;
/**
 * Con un toque al micrófono, la toma se manda sola cuando el alumno se calla
 * (Emmanuel, 15/10: «como en el modo charla»), con más margen que la charla
 * porque es una frase y se piensa. Tocar de nuevo también manda. Lo usan el
 * repaso y la hoja de la charla asistida (Emmanuel, 20/10: las tres formas en
 * todo lo de la asistida).
 */
export const TAP_SILENCE_MS = 1500;

export interface SilenceEnder {
  /** Agrega audio; `true` = ya se calló (y está armado). */
  feed: (samples: Float32Array, sampleRate: number) => boolean;
  /** Agrega una ventana ya medida (rms de `windowMs`); `true` = ya se calló (y está armado). */
  window: (rms: number, windowMs?: number) => boolean;
  /** Desde ahora corta tras `silenceMs` de silencio después de hablar. */
  arm: (silenceMs: number) => void;
}

export function createSilenceEnder(): SilenceEnder {
  let silenceMs: number | null = null;
  let peak = 0;
  let floor = Infinity;
  let voiceMs = 0;
  let quietMs = 0;
  let pending: number[] = [];
  const window = (rms: number, windowMs = WINDOW_MS): boolean => {
    if (rms > peak) peak = rms;
    if (rms < floor) floor = rms;
    const noise = Math.min(NOISE_MULT * floor, NOISE_CAP_REL * peak);
    if (rms > Math.max(VOICE_ABS, VOICE_REL * peak, noise)) {
      voiceMs += windowMs;
      quietMs = 0;
    } else {
      quietMs += windowMs;
    }
    return silenceMs !== null && voiceMs >= MIN_VOICE_MS && quietMs >= silenceMs;
  };
  return {
    arm(ms) {
      silenceMs = ms;
    },
    window,
    feed(samples, sampleRate) {
      const win = Math.max(1, Math.round((sampleRate * WINDOW_MS) / 1000));
      let ended = false;
      for (const s of samples) {
        pending.push(s);
        if (pending.length < win) continue;
        let sum = 0;
        for (const v of pending) sum += v * v;
        pending = [];
        if (window(Math.sqrt(sum / win))) ended = true;
      }
      return ended;
    },
  };
}
